import type { IParagraphManager } from '@src/slide/application/service/paragraph-manager/core/paragraph-manager.interface';
import type { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type {
    IEditorInstanceService,
} from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { IReactionDisposer } from 'mobx/lib/internal';

export interface ITabs {
    position: number;
    alignment: ETabType;
}

export interface ITextRulerProps {
    textRulerManager?: ITextRulerManager;
    scale: number;
    containerWidth: number;
    containerHeight: number;
    slideWidth: number;
    slideHeight: number;
    slideLeft: number;
    slideTop: number;
    setRef?(ref: any);
}

export enum ETabType {
    left = 'left',
    right = 'right',
    center = 'center',
    dec = 'decimal',
    bar = 'bar',
}

export interface IPaddingFrame {
    paddingLeft: number;
    paddingRight: number;
    paddingTop: number;
    paddingBottom: number;
}

export interface IRulerInfo {
    slideLeft: number;
    slideWidth: number;
    slideTop: number;
    slideHeight: number;
    scale: number;
}

export interface ITextRulerManager {
    /**
     * 依赖的Service
     */
    paragraphManager: IParagraphManager;
    menuManager: IMenuManager;
    selectionService: ISelectionService;
    editorInstanceService: IEditorInstanceService;
    /**
     * 标志符插入类型
     */
    defaultAlignment: ETabType;
    /**
     * 是否展示标尺
     */
    displayRuler: boolean;
    /**
     * 文本缩进
     */
    indentLeft: number;
    /**
     * 文本边距
     */
    marginLeft: number;
    /**
     * 文本制表符列表
     */
    tabList: Array<ITabs>;
    defaultTabs: Array<number>;
    /**
     * 水平标尺编辑区域的起始坐标，相对于标尺
     * @private
     */
    editAreaLeft: number;

    /**
     * 垂直标尺编辑区域的起始坐标，相对于标尺
     * @private
     */
    editAreaTop: number;

    /**
     * 水平标尺编辑区域的宽度
     * @private
     */
    editAreaWidth: number;

    /**
     * 垂直标尺编辑区域的高度
     * @private
     */
    editAreaHeight: number;

    /**
     * 编辑区域是否镜像翻转
     * @private
     */
    isRevert: boolean;

    /**
     * 控制当前编辑区在水平标尺还是垂直标尺，同时控制制表符和按钮
     * @private
     */
    isControlInHori: boolean;
    /**
     * 默认文本制表符间距
     */
    tabSize: number;
    rulerLeft: number;
    rulerTop: number;
    /**
     * 水平标尺宽度
     * @private
     */
    rulerWidth: number;

    /**
     * 垂直标尺高度
     * @private
     */
    rulerHeight: number;

    /**
     * 组件挂载回调
     */
    componentDidMount(props: { callback?() });

    /**
     * 组件卸载回调
     */
    componentWillUnmount();
    /**
     * 开/关标尺
     */
    updateDisplayRuler(): void;

    /**
     * 更新插入制表类型
     * @param type
     */
    updateDefaultAlignment(type: ETabType): void;

    /**
     * 更新文本缩进
     * @param changeIndent
     */
    updateIndentLeft(changeIndent): void;

    /**
     * 初始化
     * @param info
     */
    initAndFormatRulerSizeWithRealScale(info: IRulerInfo): void;

    /**
     * 计算标尺尺寸
     * @param selectedModel
     * @param scale
     */
    computeEditingRulerSize(selectedModel, scale: number): void;

    /**
     * 更新文本缩进与间距
     * @param props
     */
    updateMarginLeftAndIndentLeft(props: { changeIndent: number, changeMargin: number }): void;

    /**
     * 更新文本间距
     * @param changeMargin
     */
    updateMarginLeft(changeMargin: number): void;

    /**
     * 更新制表符
     * @param tabList
     */
    updateTabList(tabList: Array<ITabs>): void;

    /**
     * 更新默认制表间距
     */
    computeDefaultTabs(editingLength: number): Array<number>;

    /**
     * 获取文本rect
     */
    getTextRect(): { width: number, height: number};

    /**
     * 重置标尺信息
     */
    resetRulerSize();
    getLazyComponent(props: ITextRulerProps): React.ReactElement;
}
